// Journal pieces: the auto-saving note field and the per-stock shortlist box (stock page).
import { useRef } from 'react';
import { StarIcon } from 'lucide-react';
import { NSE, type Analysed } from '@planb/core';
import { since, type JournalEntry, type NewEntry } from '@/lib/client/use-journal';
import { fmtPct } from '@/charts/board';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const fmtPx = NSE.formatPrice;

/** Uncontrolled textarea that saves after a pause, so live re-renders never disturb typing. */
export function NoteField(props: { label: string; value: string; placeholder: string; onSave: (v: string) => void; id: string }) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const last = useRef(props.value);
  const save = (v: string) => { if (v !== last.current) { last.current = v; props.onSave(v); } };
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={props.id} className="text-xs font-normal text-muted-foreground">{props.label}</Label>
      <Textarea id={props.id} rows={2} defaultValue={props.value} placeholder={props.placeholder} className="min-h-16 bg-card text-[13px]"
        onChange={(e) => { const v = e.target.value; if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => save(v), 600); }}
        onBlur={(e) => { if (timer.current) clearTimeout(timer.current); save(e.target.value); }} />
    </div>
  );
}

export const sinceClass = (cls: string) => (cls === 'good' ? 'text-bull font-semibold' : cls === 'bad' ? 'text-bear font-semibold' : 'text-muted-foreground');

export function newEntry(a: Analysed, date: string, time: string): NewEntry {
  return {
    date, time, symbol: a.symbol, sector: a.sector, side: a.bull ? 'bull' : 'bear',
    priceAtAdd: a.ltp, pctAtAdd: a.pct, score: a.score, withSector: a.withSector,
  };
}

export function JournalBox(props: {
  a: Analysed; date: string; time: string; entry: JournalEntry | undefined;
  add: (e: NewEntry) => void; update: (id: string, p: { note?: string }) => void; remove: (id: string) => void;
}) {
  const { a, entry } = props;
  if (!entry) {
    return (
      <div className="grid gap-2">
        <Button className="h-11 w-full rounded-full text-sm" onClick={() => props.add(newEntry(a, props.date, props.time))}>
          <StarIcon /> Add to shortlist
        </Button>
        <p className="text-center text-xs text-muted-foreground">Save it with a note on why, then review it after the close.</p>
      </div>
    );
  }
  const s = since(entry, a.ltp);
  return (
    <div className="grid gap-3 rounded-lg border border-warn-line bg-warn-soft p-3">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-[12.5px]">
        <b className="inline-flex items-center gap-1 text-ink"><StarIcon className="size-3.5 fill-current text-warn" />In your shortlist</b>
        <span className="text-muted-foreground">since {entry.time} at {NSE.currencySymbol}{fmtPx(entry.priceAtAdd)}</span>
        <span className={cn('num', sinceClass(s.cls))}>{fmtPct(s.chg)} since</span>
        <button type="button" className="ml-auto text-xs text-muted-foreground underline hover:text-bear" onClick={() => props.remove(entry.id)}>Remove</button>
      </div>
      <NoteField key={entry.id} id={`note-${entry.id}`} label="Why did you shortlist it?" value={entry.note}
        placeholder="e.g. Moving with its sector, holding above VWAP, close to yesterday's high"
        onSave={(note) => props.update(entry.id, { note })} />
    </div>
  );
}
